import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams, type Href } from 'expo-router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { AssignAssociationModal } from './assign-association-modal';
import { AssignLotModal } from './assign-lot-modal';
import { AssignUserModal } from './assign-user-modal';
import { AutoReplyBanner } from './auto-reply-notice';
import { ComplaintPanel } from './complaint-panel';
import { ContactPlanPanel } from './contact-plan-panel';
import { MessageAttachments } from './message-attachments';
import { LabelChip } from './message-list';
import { describeMessageStatus, STATUS_ICON } from './message-status';

import { DateDisplay } from '@/components/common/date-display';
import { HtmlBody } from '@/components/common/html-body';
import { PersonChip } from '@/components/common/person-chip';
import { useComposer } from '@/components/composers/composer-host';
import { useNotification } from '@/components/notification-system';
import { ActionMenu, ActivityIndicator, Button, Divider, Modal, Text, useTheme } from '@/components/ui';
import { useApi } from '@/hooks/use-api';
import {
	useCreateApplicationFromMessage,
	useCreateWorkOrderFromMessage,
	useFetchMessageLinks,
	useLabels,
	useLinkMessage,
	useMessage,
	useRequestTypes,
	useSaveMessageToDropbox,
	useSetMessageLabel,
	useUpdateMessage,
} from '@/hooks/use-data';
import { usePermissions } from '@/hooks/use-permissions';
import { dataService, type InboxMessage, type LinkedObject } from '@/services/data-service';
import type { Association } from '@/types/models';
import { type ApiResponseError, apiErrorCode, friendlyApiMessage } from '@/utils/api-error';
import {
	ASSOCIATION_TERMINATED_CODE, terminationState, workOrderPastTermination,
} from '@/utils/association-termination';
import { dueDayMs, formatBusinessDate, todayBusinessMs } from '@/utils/date-utils';
import { AWAY_STATUSES, MESSAGE_STATUS_LABEL, type MessageStatus } from '@/utils/inbox-triage';
import { deliveryBannerFor } from '@/utils/message-delivery';
import { openPrintRoute } from '@/utils/print-navigation';
import { backOrInbox } from '@/utils/router-back';
import { WEBFLOW_FORM_ICON, WEBFLOW_FORM_LABEL, type WebflowFormType } from '@/utils/webflow-form-type';

/**
 * How long the archived message stays on screen before the pane returns to the list.
 *
 * Long enough to read "Archived" and notice the Undo beside it; short enough that it
 * reads as a hand-off rather than a hang. The issue asked for "go back after a second".
 */
const ARCHIVE_LEAVE_DELAY_MS = 1200;

/**
 * The types a human may choose, in rail order.
 *
 * `unknown` is deliberately absent: it means "the classifier could not tell", which is
 * not something a person who HAS looked at the message would ever want to assert. If they
 * genuinely cannot tell either, Correspondence is the honest answer.
 */
const MESSAGE_TYPE_CHOICES: readonly WebflowFormType[] = [
	'correspondence',
	'exterior_improvement',
	'maintenance_request',
	'reported_violation',
	'owner_questionnaire',
	'request_documents',
	'bill_pay_signup',
	'contact_us',
];

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<View style={{ flexDirection: 'row', marginBottom: 8, flexWrap: 'wrap' }}>
		<Text style={{ fontWeight: '600', width: 150, color: '#555' }}>{label}</Text>
		<View style={{ flex: 1, minWidth: 160 }}>{children}</View>
	</View>
);


/**
 * Correct what KIND of message this is (#465).
 *
 * Distinct from `RequestTypePicker` below, which chooses the ARC request type when a
 * message becomes an improvement request. This one changes what the message itself is —
 * it moves the type label and `form_type` together and creates nothing.
 *
 * Ordered with the current type first so the reader can see what they are changing FROM,
 * then Correspondence, then the rest: "this isn't a form at all" is overwhelmingly the
 * reason anyone opens this.
 */
const MessageTypePicker: React.FC<{
	visible: boolean;
	current: string | null;
	busy: boolean;
	onDismiss: () => void;
	onPick: (formType: WebflowFormType) => void;
}> = ({ visible, current, busy, onDismiss, onPick }) => {
	const theme = useTheme();
	const types = useMemo(() => {
		const rest = MESSAGE_TYPE_CHOICES.filter((t) => t !== current);
		return current && (MESSAGE_TYPE_CHOICES as readonly string[]).includes(current)
			? [current as WebflowFormType, ...rest]
			: rest;
	}, [current]);

	return (
		<Modal visible={visible} onDismiss={onDismiss}>
			<View style={{ padding: 20, minWidth: 320, maxWidth: 420 }}>
				<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 4 }}>What kind of message is this?</Text>
				<Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 12 }}>
					The type decides which label it wears and what you can create from it.
					Correspondence is just an email on the owner&rsquo;s record — no work order.
				</Text>
				<ScrollView style={{ maxHeight: 340 }}>
					{types.map((type) => {
						const active = type === current;
						return (
							<Pressable
								key={type}
								onPress={() => onPick(type)}
								disabled={busy || active}
								testID={`message-type-${type}`}
								style={{
									flexDirection: 'row', alignItems: 'center',
									paddingVertical: 12, paddingHorizontal: 10, borderRadius: 8,
									backgroundColor: active ? theme.colors.surfaceVariant : 'transparent',
									opacity: busy ? 0.6 : 1,
								}}
							>
								<Ionicons
									name={WEBFLOW_FORM_ICON[type] as keyof typeof Ionicons.glyphMap}
									size={16}
									color={theme.colors.onSurfaceVariant}
									style={{ marginRight: 10 }}
								/>
								<Text style={{ flex: 1, minWidth: 0, fontWeight: '600' }}>{WEBFLOW_FORM_LABEL[type]}</Text>
								{active ? (
									<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>current</Text>
								) : null}
							</Pressable>
						);
					})}
				</ScrollView>
				<View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 12 }}>
					{/* No backdrop-close on these modals, so an explicit way out is not optional. */}
					<Button mode="outlined" onPress={onDismiss} disabled={busy}>Cancel</Button>
				</View>
			</View>
		</Modal>
	);
};

/** Type chooser for turning a message into a request. */
const RequestTypePicker: React.FC<{
	visible: boolean;
	associationId: string | null;
	busy: boolean;
	onDismiss: () => void;
	onPick: (requestTypeId: string) => void;
}> = ({ visible, associationId, busy, onDismiss, onPick }) => {
	const theme = useTheme();
	const { data: types, isLoading } = useRequestTypes(associationId ?? '', { enabled: visible && !!associationId });

	return (
		<Modal visible={visible} onDismiss={onDismiss}>
			<View style={{ padding: 20, minWidth: 320, maxWidth: 420 }}>
				<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 4 }}>What kind of request?</Text>
				<Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 12 }}>
					The type sets the review deadline and the checklist staff work from.
				</Text>
				{isLoading ? (
					<ActivityIndicator />
				) : (
					<ScrollView style={{ maxHeight: 320 }}>
						{(types ?? []).map((type) => (
							<Pressable
								key={type.id}
								onPress={() => onPick(type.id)}
								disabled={busy}
								testID={`request-type-${type.id}`}
								style={{ paddingVertical: 12, paddingHorizontal: 10, borderRadius: 8 }}
							>
								<Text style={{ fontWeight: '600' }}>{type.name}</Text>
							</Pressable>
						))}
					</ScrollView>
				)}
				<View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 12 }}>
					{/* These modals never close on a backdrop tap, so an explicit way out is
						 not optional. */}
					<Button mode="outlined" onPress={onDismiss} disabled={busy}>Cancel</Button>
				</View>
			</View>
		</Modal>
	);
};

/**
 * `/inbox/<id>` — one message, forever (#369).
 *
 * The address is permanent: re-classifying, labelling or turning the message into a work
 * order changes labels and links, never the row. So this pane has to answer "what is this,
 * and what did it become?" for a message in any state, which is why the LINKED OBJECTS
 * come first — that is the part a staffer arriving from a stale link most needs.
 *
 * The whole thread is shown with THIS message highlighted, because an email out of its
 * conversation is usually unreadable.
 */
export const InboxMessagePane: React.FC<{ messageId: string }> = ({ messageId }) => {
	const theme = useTheme();
	const { showModal, showToast } = useNotification();
	const { canUseInbox: canUpdate, isWorkOrderAdmin } = usePermissions();

	// The label the reader came from, so Back returns to that queue rather than
	// dumping them in All mail.
	const routeParams = useLocalSearchParams<{ label?: string }>();
	const fromLabel = typeof routeParams.label === 'string' ? routeParams.label : null;

	const { data: message, isLoading } = useMessage(messageId);
	const { data: labelRegistry } = useLabels();
	const allLabels = labelRegistry?.labels;
	const updateMessage = useUpdateMessage();
	const setLabel = useSetMessageLabel();
	const createApplication = useCreateApplicationFromMessage();
	const createWorkOrder = useCreateWorkOrderFromMessage();
	const linkMessage = useLinkMessage();
	const fetchLinks = useFetchMessageLinks();
	const [labelPickerOpen, setLabelPickerOpen] = useState(false);
	const [typePickerOpen, setTypePickerOpen] = useState(false);
	const [formTypePickerOpen, setFormTypePickerOpen] = useState(false);
	/**
	 * The composers are NOT rendered here — `ComposerProvider` owns them at the app root.
	 *
	 * Two reasons, both learned from bugs. A composer rendered by this pane died with it: a
	 * minimized reply vanished the moment the user navigated to the dashboard, taking the
	 * draft with it. And with no backdrop both toolbar buttons stay live, so Reply and
	 * Forward could be open at once — docked at identical coordinates so one was invisible,
	 * each with its own Cmd+Enter listener on `window` (and `stopPropagation()` does not stop
	 * sibling listeners on the same target, so one keystroke fired BOTH submits). The host
	 * keeps exactly one open, above the router.
	 */
	const composer = useComposer();
	/**
	 * A reply that archives has to leave the reader somewhere sensible, and only this pane
	 * knows where: a linked improvement request if there is one, else the label they came
	 * from. It is handed over at open time because the host outlives this component — by the
	 * time the letter is sent, a callback into here may have nothing to call.
	 */
	const openReply = () => composer.open({
		kind: 'reply',
		messageId,
		afterArchive: {
			// Optional-chained because this is defined above the `!message` guard; it can only
			// ever be CALLED from the loaded pane.
			href: message?.linkedObjects?.find((o) => o.objectType === 'exterior_application' && !o.missing)?.href ?? null,
			label: fromLabel ?? message?.labels?.[0]?.slug ?? null,
		},
	});
	const openForward = () => composer.open({ kind: 'forward', messageId });
	const saveToDropbox = useSaveMessageToDropbox();
	const [assignLotOpen, setAssignLotOpen] = useState(false);
	const [assignAssociationOpen, setAssignAssociationOpen] = useState(false);
	const [assignUserOpen, setAssignUserOpen] = useState(false);
	// For "(me)" and the me-first ordering in the assignee picker.
	const { user: currentUser } = useApi();

	/**
	 * Mark read on open. `read_at` (seen) is separate from labels (dealt with), so opening
	 * clears the unread dot without claiming the work is done.
	 *
	 * The ref guards a re-fire on refetch: `message` changes identity every refetch and
	 * without this the mutation would loop.
	 */
	const markedRead = useRef<string | null>(null);
	useEffect(() => {
		if (!message || message.readAt) return;
		if (markedRead.current === messageId) return;
		markedRead.current = messageId;
		updateMessage.mutate({ id: messageId, body: { read: true } });
	}, [message, messageId, updateMessage]);

	/**
	 * The pending "return to the list" after an archive (#467).
	 *
	 * Held in a ref so Undo and unmount can both cancel it. Navigating out of a component
	 * that a timer is about to navigate again is how you end up two screens back from
	 * where you asked to be.
	 */
	const leaveAfterArchive = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => () => {
		if (leaveAfterArchive.current) clearTimeout(leaveAfterArchive.current);
	}, []);

	if (isLoading) {
		return (
			<View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
				<ActivityIndicator />
			</View>
		);
	}

	if (!message) {
		return (
			<View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 }}>
				<Ionicons name="alert-circle-outline" size={40} color={theme.colors.outline} />
				<Text style={{ marginTop: 12, color: theme.colors.onSurfaceVariant }}>This message no longer exists.</Text>
			</View>
		);
	}

	const fail = (error: unknown, fallback: string) =>
		showModal({ title: 'Error', message: friendlyApiMessage(error, fallback), variant: 'error' });

	const toggleLabel = async (slug: string, on: boolean) => {
		try {
			await setLabel.mutateAsync({ id: messageId, slug, on });
		} catch (error) {
			fail(error, 'Could not change the label.');
		}
	};

	/**
	 * Back to wherever the reader came from — the request that owns this message if there
	 * is one, otherwise the queue they arrived from.
	 *
	 * Extracted so the archive hand-off below returns to the same place the Back link
	 * would, rather than inventing a second answer to the same question.
	 */
	const goBack = () => {
		const parent = message.linkedObjects?.find((o) => o.objectType === 'exterior_application' && !o.missing);
		if (parent) { router.push(parent.href as Href); return; }
		backOrInbox(fromLabel ?? message.labels[0]?.slug ?? null);
	};

	/**
	 * Archive, then say so and get out of the way (#467).
	 *
	 * The old version mutated and left the reader sitting on a message that had silently
	 * vanished from the list behind it — the only feedback was a toast that said
	 * "Archived" and then disappeared, and there was no way back if it was a mistake. Three
	 * things fix that: the banner this sets up (rendered below), an Undo on the toast, and
	 * a SHORT delay before leaving so the confirmation is actually seen. Leaving instantly
	 * would put the reader back in the list before they could register what happened, which
	 * is the complaint that opened the issue.
	 *
	 * Un-archiving deliberately does NOT navigate: the reader just brought this message
	 * back, so it is what they want to be looking at.
	 */
	const moveTo = async (next: MessageStatus) => {
		const previous = message.status ?? 'open';
		if (previous === next) return;
		try {
			await updateMessage.mutateAsync({ id: messageId, body: { status: next } });
		} catch (error) {
			fail(error, `Could not move this message to ${MESSAGE_STATUS_LABEL[next]}.`);
			return;
		}

		/**
		 * Coming BACK to the inbox does not navigate: the reader just retrieved this message,
		 * so it is exactly what they want to be looking at.
		 */
		if (next === 'open') {
			showToast('Moved back to the inbox', { variant: 'success' });
			return;
		}

		const undo = async () => {
			if (leaveAfterArchive.current) {
				clearTimeout(leaveAfterArchive.current);
				leaveAfterArchive.current = null;
			}
			try {
				await updateMessage.mutateAsync({ id: messageId, body: { status: previous } });
				showToast('Moved back', { variant: 'success' });
			} catch (error) {
				fail(error, 'Could not undo that.');
			}
		};

		showToast(MESSAGE_STATUS_LABEL[next], {
			variant: 'success',
			// Longer than the 4s default: an Undo nobody has time to read is decoration.
			duration: 8000,
			actions: [{ label: 'Undo', onPress: () => { void undo(); } }],
		});
		leaveAfterArchive.current = setTimeout(() => {
			leaveAfterArchive.current = null;
			goBack();
		}, ARCHIVE_LEAVE_DELAY_MS);
	};

	/** The banner's "Move to inbox" and the old archive toggle, in one place. */
	const archive = () => moveTo((message.status ?? 'open') === 'open' ? 'archived' : 'open');

	/**
	 * Correct the classifier (#465). The server moves the label and `form_type` together;
	 * the pane just has to reflect it, which the mutation's invalidation handles.
	 */
	const changeFormType = async (formType: WebflowFormType) => {
		setFormTypePickerOpen(false);
		try {
			await updateMessage.mutateAsync({ id: messageId, body: { formType } });
			showToast(`Changed to ${WEBFLOW_FORM_LABEL[formType]}`, { variant: 'success' });
		} catch (error) {
			fail(error, 'Could not change the type of this message.');
		}
	};

	/**
	 * File the attachments in Dropbox without emailing anyone (#466).
	 *
	 * Reports what it SKIPPED as well as what it saved — a silent "Saved" over a message
	 * whose oversized photo never made it is the failure this whole result shape exists to
	 * prevent.
	 */
	const onSaveToDropbox = async () => {
		try {
			const result = await saveToDropbox.mutateAsync(messageId);
			const savedText = `${result.saved.length} file${result.saved.length === 1 ? '' : 's'} filed under ${result.target}`;
			if (result.skipped.length > 0) {
				showModal({
					title: 'Saved, with some files skipped',
					message: `${savedText}.\n\nNot saved:\n${result.skipped.map((s) => `• ${s.fileName} — ${s.reason}`).join('\n')}`,
					variant: 'warning',
				});
				return;
			}
			showToast(savedText, { variant: 'success' });
		} catch (error) {
			fail(error, 'Could not save these files to Dropbox.');
		}
	};

	const applied = new Set(message.labels.map((l) => l.slug));
	// Already produced something? Then the action is "open it" (the PART OF panel), not
	// "make another".
	const existingRequest = message.linkedObjects.find((l) => l.objectType === 'exterior_application');
	const existingWorkOrder = message.linkedObjects.find((l) => l.objectType === 'work_order');
	const fieldEntries = Object.entries(message.fields ?? {});

	const createWorkOrderNow = async (terminationAck = false) => {
		try {
			const created = await createWorkOrder.mutateAsync({
				id: messageId,
				body: terminationAck ? { terminationAck: true } : undefined,
			});
			showToast(`Work order ${created.orderNumber != null ? `#${created.orderNumber}` : ''} created`.trim(), { variant: 'success' });
			router.push(`/work-orders/${created.workOrderId}` as Href);
		} catch (error) {
			// The date was set after we looked: a work-order admin may still confirm and resend.
			if (!terminationAck && apiErrorCode(error) === ASSOCIATION_TERMINATED_CODE
				&& (error as ApiResponseError).body?.canOverride === true) {
				confirmTermination(friendlyApiMessage(error), true);
				return;
			}
			fail(error, 'Could not create the work order.');
		}
	};

	const confirmTermination = (text: string, past: boolean) => {
		showModal({
			title: past ? 'Association terminated' : 'Association terminating soon',
			message: text,
			variant: 'warning',
			actions: [
				{ label: 'Cancel', style: 'default', onPress: () => {} },
				{ label: 'Yes, create it', style: 'destructive', onPress: () => { void createWorkOrderNow(true); } },
			],
		});
	};

	/**
	 * Same termination-date call-out as the work-order form (#417): a terminating association
	 * asks first; past its date only a work-order admin may confirm (the server enforces it).
	 * The association is read on click — one request, and only when someone acts.
	 */
	const onCreateWorkOrder = async () => {
		let association: Association | null = null;
		if (message.associationId) {
			try {
				association = await dataService.getAssociation(message.associationId);
			} catch (error) {
				// Never read a failed lookup as "no termination date" — that would skip the warning,
				// and the server deliberately allows work orders BEFORE the date.
				fail(error, 'Could not check the association — try again.');
				return;
			}
		}
		const today = todayBusinessMs();
		if (!association || terminationState(association.terminationDate, today) === 'none') {
			await createWorkOrderNow();
			return;
		}
		const day = formatBusinessDate(dueDayMs(association.terminationDate));
		const past = workOrderPastTermination(association.terminationDate, today);
		if (past && !isWorkOrderAdmin) {
			showModal({
				title: 'Association terminated',
				message: `${association.name} terminates on ${day}. Work orders past that date need an admin.`,
				variant: 'error',
			});
			return;
		}
		confirmTermination(`${association.name} terminates on ${day}. Are you sure this work order belongs here?`, past);
	};

	/**
	 * One primary action, chosen by what the message IS.
	 *
	 * An Owner Questionnaire's own action lives in its ContactPlanPanel below (it needs the
	 * preview beside it), so this returns nothing for that type rather than duplicating the
	 * button somewhere it has no context.
	 */
	const primaryAction: { label: string; icon: keyof typeof Ionicons.glyphMap; testID: string; busy: boolean; onPress: () => void } | null
		= !canUpdate || !message.associationId
			? null
			: message.formType === 'exterior_improvement' && !existingRequest
				? {
					label: 'Create improvement request',
					icon: 'construct-outline',
					testID: 'btn-create-application',
					busy: createApplication.isPending,
					onPress: () => setTypePickerOpen(true),
				}
				: message.formType === 'maintenance_request' && !existingWorkOrder
					? {
						label: 'Create work order',
						icon: 'hammer-outline',
						testID: 'btn-create-work-order',
						busy: createWorkOrder.isPending,
						onPress: onCreateWorkOrder,
					}
					: null;

	return (
		<>
			<ScrollView style={{ flex: 1, padding: 16 }} testID="inbox-message-pane">
				{/*
					Back to the list. An explicit control rather than relying on the browser
					chrome: on desktop the list and the message share one screen, so there is
					no visual affordance for "leave this" without it.
				*/}
				{/*
					When this message belongs to a request, back goes to the REQUEST — that is
					where the reader came from and what they were working. Falling back to a
					label queue drops them somewhere they have to navigate out of again. Only
					when it belongs to nothing is the label the right answer. See `goBack`.
				*/}
				<Pressable
					onPress={goBack}
					testID="btn-inbox-back"
					// Real padding, not hitSlop — RN-Web's Pressable drops hitSlop.
					style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', paddingVertical: 6, paddingRight: 10, marginBottom: 6 }}
				>
					<Ionicons name="arrow-back" size={18} color={theme.colors.primary} style={{ marginRight: 6 }} />
					<Text style={{ color: theme.colors.primary, fontWeight: '600' }}>
						{fromLabel
							? (message.labels.find((l) => l.slug === fromLabel)?.name ?? 'Inbox')
							: 'Inbox'}
					</Text>
				</Pressable>

				{/*
					A PERSISTENT marker, not just a toast (#467).

					A toast is gone in four seconds, so anyone arriving at this message later —
					from a thread, a search result, or the Archived view — had no way to tell it
					was archived except by reading the verb on the button at the bottom of the
					page. State it at the top, next to the thing it applies to, and put the way
					out right beside it.
				*/}
				{/*
					Now covers every away-state, and NAMES THE PERSON (#534).
					`archived_at` recorded the instant but never the actor, so "who archived
					this?" was unanswerable from the record. `statusBy` is NULL for rows
					archived before migration 261 and for anything inbound mail moved, so the
					name is appended only when there is one rather than printing "by unknown".
				*/}
				{message.status && message.status !== 'open' ? (
					<View
						testID="message-archived-banner"
						style={{
							flexDirection: 'row',
							alignItems: 'center',
							paddingVertical: 8,
							paddingHorizontal: 12,
							marginBottom: 10,
							borderRadius: 8,
							backgroundColor: theme.colors.surfaceVariant,
						}}
					>
						<Ionicons
							name={STATUS_ICON[message.status]}
							size={16}
							color={theme.colors.onSurfaceVariant}
							style={{ marginRight: 8 }}
						/>
						<Text style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: '600', color: theme.colors.onSurfaceVariant }}>
							{describeMessageStatus(message)}
						</Text>
						{canUpdate ? (
							<Pressable
								onPress={() => { void moveTo('open'); }}
								testID="btn-unarchive-banner"
								style={{ paddingVertical: 4, paddingHorizontal: 8 }}
							>
								<Text style={{ fontSize: 13, fontWeight: '700', color: theme.colors.primary }}>Move to inbox</Text>
							</Pressable>
						) : null}
					</View>
				) : null}

				{/*
					ONE delivery banner, #595's: an inbox rule that wrote back on its own (inbound), or
					— for a letter a PM composed (#555) — that letter's own held / test-mailbox state
					(outbound). Above the subject: neither may read as a normal, delivered email.
					`deliveryBannerFor` decides which, so the two can never contradict each other.
				*/}
				{(() => {
					const banner = deliveryBannerFor(message);
					return banner ? (
						<AutoReplyBanner
							attempts={banner.attempts}
							noun={banner.noun}
							idPrefix={banner.noun === 'Letter' ? 'message-letter-delivery' : 'message-auto-reply'}
						/>
					) : null;
				})()}

				<Text style={{ fontSize: 22, fontWeight: '700' }} numberOfLines={3}>
					{message.subject || '(no subject)'}
				</Text>

				<View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
					{message.labels.map((label) => (
						<LabelChip key={label.id} label={label} />
					))}
					{canUpdate ? (
						<Pressable
							onPress={() => setLabelPickerOpen(true)}
							testID="btn-edit-labels"
							// Real padding: RN-Web drops hitSlop.
							style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 8 }}
						>
							<Ionicons name="pricetag-outline" size={14} color={theme.colors.primary} style={{ marginRight: 4 }} />
							<Text style={{ fontSize: 12, fontWeight: '600', color: theme.colors.primary }}>Labels</Text>
						</Pressable>
					) : null}
				</View>

				{/*
					What this message BECAME. First, because a staffer arriving from an old link
					most needs to know the work order already exists — that is the whole reason
					the permalink is stable.
				*/}
				{message.linkedObjects.length > 0 ? (
					<View
						style={{
							marginTop: 14,
							padding: 12,
							borderRadius: 8,
							borderWidth: 1,
							borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
							backgroundColor: theme.colors.surfaceVariant,
						}}
						testID="message-linked-objects"
					>
						<Text style={{ fontSize: 12, fontWeight: '700', letterSpacing: 0.4, color: theme.colors.onSurfaceVariant, marginBottom: 6 }}>
							PART OF
						</Text>
						{message.linkedObjects.map((linked: LinkedObject) => (
							<Pressable
								key={`${linked.objectType}-${linked.objectId}`}
								onPress={() => linked.href && router.push(linked.href as Href)}
								disabled={!linked.href}
								testID={`linked-${linked.objectType}-${linked.objectId}`}
								style={{ paddingVertical: 6 }}
							>
								<Text style={{ fontWeight: '600', color: linked.missing ? theme.colors.error : theme.colors.primary }}>
									{linked.title}
								</Text>
								{linked.subtitle ? (
									<Text numberOfLines={1} style={{ color: theme.colors.onSurfaceVariant }}>{linked.subtitle}</Text>
								) : null}
							</Pressable>
						))}
					</View>
				) : null}

				{/*
					"They already have these — is this one of them?"
					
					The single most valuable thing on this pane. Without it a staffer reading a
					forwarded reply cannot tell a new request from a follow-up, and the default
					behaviour of a busy person is to create another one. Shown ABOVE the create
					button for exactly that reason: linking has to be the easier move.
				*/}
				{canUpdate && message.relatedRequests.length > 0 ? (
					<View
						style={{
							marginTop: 14,
							padding: 12,
							borderRadius: 8,
							borderWidth: 1,
							borderColor: theme.colors.primary,
							backgroundColor: theme.colors.surfaceVariant,
						}}
						testID="related-requests"
					>
						<Text style={{ fontSize: 12, fontWeight: '700', letterSpacing: 0.4, color: theme.colors.onSurfaceVariant, marginBottom: 6 }}>
							{message.relatedRequests.length === 1
								? 'THIS LOT ALREADY HAS A REQUEST — IS THIS PART OF IT?'
								: `THIS LOT ALREADY HAS ${message.relatedRequests.length} REQUESTS — IS THIS ONE OF THEM?`}
						</Text>
						{message.relatedRequests.map((candidate) => (
							<View
								key={candidate.id}
								style={{
									flexDirection: 'row', alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 6,
									paddingVertical: 8,
									borderTopWidth: 1,
									borderTopColor: theme.colors.outlineVariant ?? theme.colors.outline,
								}}
							>
								<Pressable
									onPress={() => router.push(`/exterior-applications/${candidate.id}` as Href)}
									style={{ flex: 1, minWidth: 180, marginRight: 8, paddingVertical: 2 }}
									testID={`related-request-${candidate.id}`}
								>
									<Text style={{ fontWeight: '600' }} numberOfLines={1}>
										{candidate.applicationNumber != null ? `#${candidate.applicationNumber} · ` : ''}
										{candidate.requestTypeName || 'Uncategorized'}
									</Text>
									{candidate.description ? (
										<Text style={{ color: theme.colors.onSurfaceVariant }} numberOfLines={2}>{candidate.description}</Text>
									) : null}
									<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant, marginTop: 2 }}>{candidate.statusLabel}</Text>
								</Pressable>
								<Pressable
									onPress={async () => {
										try {
											await linkMessage.mutateAsync({ id: messageId, objectType: 'exterior_application', objectId: candidate.id });
											showToast('Filed onto that request', { variant: 'success' });
										} catch (error) {
											fail(error, 'Could not link this message.');
										}
									}}
									disabled={linkMessage.isPending}
									testID={`btn-link-request-${candidate.id}`}
									style={{
										flexDirection: 'row', alignItems: 'center',
										paddingVertical: 8, paddingHorizontal: 12,
										borderRadius: 8, backgroundColor: theme.colors.primary,
									}}
								>
									<Ionicons name="link-outline" size={14} color={theme.colors.onPrimary} style={{ marginRight: 4 }} />
									<Text style={{ fontWeight: '700', color: theme.colors.onPrimary }}>This one</Text>
								</Pressable>
							</View>
						))}
					</View>
				) : null}

				{canUpdate ? (
					<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
						{/*
							The primary action is per FORM TYPE. Offering "Create improvement
							request" on an Owner Questionnaire is the same mistake as the old
							suggestion chips: the subject already told us what this is, so the
							button should follow it rather than assume every message is an ARC
							request.

							Each is hidden once it has been done — the PART OF panel links to
							whatever it produced.
						*/}
						{primaryAction ? (
							<Pressable
								onPress={primaryAction.onPress}
								disabled={primaryAction.busy}
								testID={primaryAction.testID}
								style={{
									flexDirection: 'row', alignItems: 'center',
									paddingVertical: 8, paddingHorizontal: 12,
									borderRadius: 8, backgroundColor: theme.colors.primary,
								}}
							>
								<Ionicons name={primaryAction.icon} size={16} color={theme.colors.onPrimary} style={{ marginRight: 6 }} />
								<Text style={{ fontWeight: '700', color: theme.colors.onPrimary }}>{primaryAction.label}</Text>
							</Pressable>
						) : null}
						{/*
							Answer the homeowner (#532).

							Promoted to the front of the bar, beside the primary action, because
							it is the most common thing anyone needs to do with a message that is
							NOT going to become a work order — and "this is the owner's own
							responsibility" is the single most frequent reply the office sends.
							Forward sits further along on purpose: it talks to colleagues and
							vendors, which is rarer and is not what a homeowner is waiting for.
						*/}
						<Pressable
							onPress={openReply}
							testID="btn-reply-message"
							style={{
								flexDirection: 'row', alignItems: 'center',
								paddingVertical: 8, paddingHorizontal: 12,
								borderRadius: 8, borderWidth: 1,
								borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
							}}
						>
							<Ionicons name="arrow-undo-outline" size={16} color={theme.colors.onSurfaceVariant} style={{ marginRight: 6 }} />
							<Text style={{ fontWeight: '600', color: theme.colors.onSurface }}>Reply</Text>
						</Pressable>
						{/*
							Overrule the classifier (#465).

							The subject is a good guess and a guess is all it is: homeowners pick
							the wrong form, and sometimes a form for work the company does not do.
							Without this the pane offered "Create work order" on a chipmunk
							remediation request forever, and the only alternative was to archive
							a message that genuinely belongs on the owner's record.
						*/}
						<Pressable
							onPress={() => setFormTypePickerOpen(true)}
							testID="btn-change-message-type"
							style={{
								flexDirection: 'row', alignItems: 'center',
								paddingVertical: 8, paddingHorizontal: 12,
								borderRadius: 8, borderWidth: 1,
								borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
							}}
						>
							<Ionicons name="swap-horizontal-outline" size={16} color={theme.colors.onSurfaceVariant} style={{ marginRight: 6 }} />
							<Text style={{ fontWeight: '600', color: theme.colors.onSurface }}>Change type</Text>
						</Pressable>
						{/*
							A paper copy for the board packet, the vendor, or the physical file
							(#465). Through `openPrintRoute` — never router.push, which resolves a
							nested print route to a different member of the print group.
						*/}
						<Pressable
							onPress={() => openPrintRoute(`/print/message/${messageId}`, (u) => router.push(u as Href))}
							testID="btn-print-message"
							style={{
								flexDirection: 'row', alignItems: 'center',
								paddingVertical: 8, paddingHorizontal: 12,
								borderRadius: 8, borderWidth: 1,
								borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
							}}
						>
							<Ionicons name="print-outline" size={16} color={theme.colors.onSurfaceVariant} style={{ marginRight: 6 }} />
							<Text style={{ fontWeight: '600', color: theme.colors.onSurface }}>Print</Text>
						</Pressable>
						{/* Hand it to someone who has to act on it outside the app (#466). */}
						<Pressable
							onPress={openForward}
							testID="btn-forward-message"
							style={{
								flexDirection: 'row', alignItems: 'center',
								paddingVertical: 8, paddingHorizontal: 12,
								borderRadius: 8, borderWidth: 1,
								borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
							}}
						>
							<Ionicons name="arrow-redo-outline" size={16} color={theme.colors.onSurfaceVariant} style={{ marginRight: 6 }} />
							<Text style={{ fontWeight: '600', color: theme.colors.onSurface }}>Forward</Text>
						</Pressable>
						{/*
							Filing without emailing. Offered only when there is something to file —
							a button that reports "0 files saved" is a button that should not have
							been there.
						*/}
						{message.attachments.length > 0 ? (
							<Pressable
								onPress={onSaveToDropbox}
								disabled={saveToDropbox.isPending}
								testID="btn-save-to-dropbox"
								style={{
									flexDirection: 'row', alignItems: 'center',
									paddingVertical: 8, paddingHorizontal: 12,
									borderRadius: 8, borderWidth: 1,
									borderColor: theme.colors.outlineVariant ?? theme.colors.outline,
									opacity: saveToDropbox.isPending ? 0.6 : 1,
								}}
							>
								<Ionicons name="cloud-upload-outline" size={16} color={theme.colors.onSurfaceVariant} style={{ marginRight: 6 }} />
								<Text style={{ fontWeight: '600', color: theme.colors.onSurface }}>
									{saveToDropbox.isPending ? 'Saving…' : 'Save to Dropbox'}
								</Text>
							</Pressable>
						) : null}
						{/*
							The three ways a message LEAVES the inbox, in one control.

							They are mutually exclusive by construction — `messages.status` holds
							exactly one value — so a menu mirrors the data model in a way three
							sibling buttons would not. It also keeps the bar at six controls;
							eight equal-weight buttons say nothing about which is the common path.

							The current state is excluded from its own menu: "Archive" on an
							already-archived message is a no-op the server would reject anyway.
						*/}
						<ActionMenu
							label="Move to…"
							icon="folder-open-outline"
							testID="btn-move-message"
							actions={AWAY_STATUSES
								.filter((status) => status !== (message.status ?? 'open'))
								.map((status) => ({
									label: MESSAGE_STATUS_LABEL[status],
									icon: STATUS_ICON[status],
									destructive: status === 'spam',
									testID: `btn-move-${status}`,
									onPress: () => { void moveTo(status); },
								}))}
						/>
					</View>
				) : null}

				{/* The Owner Questionnaire's action, shown WITH its preview — the plan is the
					 point, not the button. */}
				{message.formType === 'owner_questionnaire' ? (
					<ContactPlanPanel messageId={messageId} canApply={canUpdate} />
				) : null}

				<Divider style={{ marginVertical: 16 }} />

				<Row label="From">
					<Text>{message.submitterName || message.fromName || '—'}</Text>
					{message.fromAddress ? (
						<Text style={{ color: theme.colors.onSurfaceVariant }}>{message.fromAddress}</Text>
					) : null}
					{message.submitterPhone ? (
						<Text style={{ color: theme.colors.onSurfaceVariant }}>{message.submitterPhone}</Text>
					) : null}
				</Row>
				<Row label="Property">
					<Text>{message.propertyAddress || '—'}</Text>
				</Row>
				<Row label="Homeowner / Lot">
					<View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
						{message.accountId ? (
							<Pressable
								onPress={() => router.push(`/accounting/account/${message.accountId}` as Href)}
								accessibilityRole="link"
								testID="link-message-account"
								style={{ paddingVertical: 2, marginRight: 8 }}
							>
								<Text style={{ color: theme.colors.primary }}>
									{message.accountOwnerName || 'Matched lot'}
									{message.accountNumber ? ` · ${message.accountNumber}` : ''}
								</Text>
							</Pressable>
						) : (
							<Text style={{ color: theme.colors.error, marginRight: 8 }}>Needs Owner</Text>
						)}
						{/*
							Always offered, matched or not. An unmatched forward is the case that
							needs this most, and a WRONG match needs correcting just as much as a
							missing one needs filling.
						*/}
						{canUpdate ? (
							<Pressable
								onPress={() => setAssignLotOpen(true)}
								testID="btn-assign-lot"
								// Real padding, not hitSlop — RN-Web's Pressable drops hitSlop.
								style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 4, paddingHorizontal: 6 }}
							>
								<Ionicons name="create-outline" size={14} color={theme.colors.primary} style={{ marginRight: 4 }} />
								<Text style={{ fontSize: 12, fontWeight: '600', color: theme.colors.primary }}>
									{message.accountId ? 'Change' : 'Assign a lot'}
								</Text>
							</Pressable>
						) : null}
					</View>
				</Row>
				{/*
					Which association owns this, and who manages it — plus a way to set it
					WITHOUT naming a lot. That is the case support hits constantly: they can
					see whose association it is but not which owner sent it, and before this
					the only way off the unmatched pile was to guess a lot.
				*/}
				<Row label="Association">
					<View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
						<Text
							style={{
								fontWeight: message.associationName ? '700' : undefined,
								color: message.associationName ? undefined : theme.colors.onSurfaceVariant,
							}}
						>
							{message.associationName ?? 'Not matched'}
						</Text>
						{/* The same chip the list rows use: a PM has to be recognisable at a
								glance on both surfaces, or the list teaches a code the pane breaks. */}
						<PersonChip
							id={message.managerId}
							name={message.managerName}
							color={message.managerColor}
							size={14}
						/>
						{canUpdate ? (
							<Pressable
								onPress={() => setAssignAssociationOpen(true)}
								testID="btn-assign-association"
								style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 4, paddingHorizontal: 6 }}
							>
								<Ionicons name="business-outline" size={14} color={theme.colors.primary} style={{ marginRight: 4 }} />
								<Text style={{ fontSize: 12, fontWeight: '600', color: theme.colors.primary }}>
									{message.associationId ? 'Change' : 'Set association'}
								</Text>
							</Pressable>
						) : null}
					</View>
				</Row>
				{/*
					Who is dealing with it. Sits next to the association because the two
					questions are asked together — "whose is this, and who has it?" — but
					they are independent: an unmatched message can still have a name on it.
				*/}
				<Row label="Assigned to">
					<View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
						{message.assignedTo ? (
							<PersonChip
								id={message.assignedTo}
								name={message.assignedToName}
								color={message.assignedToColor}
								size={14}
								fallback="Unknown user"
							/>
						) : (
							<Text style={{ color: theme.colors.onSurfaceVariant }}>Nobody</Text>
						)}
						{canUpdate ? (
							<Pressable
								onPress={() => setAssignUserOpen(true)}
								testID="btn-assign-user"
								style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 4, paddingHorizontal: 6 }}
							>
								<Ionicons name="person-add-outline" size={14} color={theme.colors.primary} style={{ marginRight: 4 }} />
								<Text style={{ fontSize: 12, fontWeight: '600', color: theme.colors.primary }}>
									{message.assignedTo ? 'Change' : 'Assign'}
								</Text>
							</Pressable>
						) : null}
					</View>
				</Row>
				{/* Who the complaint is ABOUT, and whether the board has seen it (#566). Keyed
				    on the label, not `formType`, because staff re-label by hand. */}
				{applied.has('complaint') ? (
					<ComplaintPanel
						messageId={messageId}
						associationId={message.associationId}
						reportedToBoardAt={message.reportedToBoardAt}
						canUpdate={canUpdate}
					/>
				) : null}
				{/* Kept even after matching, so a BAD match can be understood later. */}
				{message.associationNameRaw ? (
					<Row label="Association as typed"><Text>{message.associationNameRaw}</Text></Row>
				) : null}
				{message.matchSource ? <Row label="Matched by"><Text>{message.matchSource}</Text></Row> : null}
				{message.createdAt ? (
					<Row label="Received"><DateDisplay date={new Date(message.createdAt)} /></Row>
				) : null}

				{fieldEntries.length > 0 ? (
					<>
						<Text style={{ fontSize: 18, fontWeight: '700', marginTop: 12, marginBottom: 8 }}>Form Answers</Text>
						{fieldEntries.map(([key, value]) => (
							<Row key={key} label={key}><Text>{value || '—'}</Text></Row>
						))}
					</>
				) : null}

				<Divider style={{ marginVertical: 16 }} />

				{/*
					The conversation, with THIS message highlighted. An email read outside its
					thread is usually meaningless — and after a re-classify the thread is the only
					thing that still shows the history.
				*/}
				<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 8 }}>
					Conversation{message.thread.length > 1 ? ` (${message.thread.length})` : ''}
				</Text>
				{message.thread.map((entry: InboxMessage) => {
					const isCurrent = entry.id === message.id;
					return (
						<Pressable
							key={entry.id}
							onPress={() => !isCurrent && router.push(`/inbox/${entry.id}` as Href)}
							disabled={isCurrent}
							testID={`thread-message-${entry.id}`}
							style={{
								padding: 12,
								marginBottom: 8,
								borderRadius: 8,
								borderWidth: isCurrent ? 2 : 1,
								borderColor: isCurrent ? theme.colors.primary : (theme.colors.outlineVariant ?? theme.colors.outline),
								backgroundColor: isCurrent ? theme.colors.primaryContainer : theme.colors.surface,
							}}
						>
							<View style={{ flexDirection: 'row', alignItems: 'center' }}>
								<Text style={{ fontWeight: '700', flex: 1, minWidth: 0 }} numberOfLines={1}>
									{entry.fromName || entry.fromAddress || 'Unknown sender'}
									{isCurrent ? '  ← this message' : ''}
								</Text>
								{entry.createdAt ? (
									<DateDisplay date={new Date(entry.createdAt)} variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }} />
								) : null}
							</View>
							{isCurrent ? (
								<View style={{ marginTop: 8 }}>
									<HtmlBody html={message.bodyHtml ?? null} fallbackText={message.bodyText ?? ''} />
									<MessageAttachments
										attachments={message.attachments ?? []}
										pendingUploadLinks={message.pendingUploadLinks ?? []}
										canFetch={canUpdate}
										fetching={fetchLinks.isPending}
										onError={fail}
										onFetchLinks={async () => {
											try {
												const outcome = await fetchLinks.mutateAsync(messageId);
												const failedCount = outcome.failed.length;
												showToast(
													failedCount > 0
														? `Saved ${outcome.downloaded}; ${failedCount} could not be fetched`
														: `Saved ${outcome.downloaded} file${outcome.downloaded === 1 ? '' : 's'}`,
													{ variant: failedCount > 0 ? 'warning' : 'success' },
												);
											} catch (error) {
												fail(error, 'Could not save those files.');
											}
										}}
									/>
								</View>
							) : (
								<Text numberOfLines={2} style={{ color: theme.colors.onSurfaceVariant, marginTop: 4 }}>
									{entry.bodyText || entry.subject || '—'}
								</Text>
							)}
						</Pressable>
					);
				})}
			</ScrollView>

			<AssignUserModal
				visible={assignUserOpen}
				currentUserId={currentUser?.id}
				assignedTo={message.assignedTo}
				busy={updateMessage.isPending}
				onDismiss={() => setAssignUserOpen(false)}
				onAssign={async (userId) => {
					try {
						await updateMessage.mutateAsync({ id: messageId, body: { assignedTo: userId } });
						setAssignUserOpen(false);
						showToast(userId ? 'Assigned' : 'Unassigned', { variant: 'success' });
					} catch (error) {
						fail(error, 'Could not assign the message.');
					}
				}}
			/>

			<AssignAssociationModal
				visible={assignAssociationOpen}
				associationNameRaw={message.associationNameRaw}
				currentAssociationId={message.associationId}
				busy={updateMessage.isPending}
				onDismiss={() => setAssignAssociationOpen(false)}
				onAssign={async (associationId) => {
					try {
						await updateMessage.mutateAsync({ id: messageId, body: { associationId } });
						setAssignAssociationOpen(false);
						showToast(associationId ? 'Association set' : 'Association cleared', { variant: 'success' });
					} catch (error) {
						fail(error, 'Could not set the association.');
					}
				}}
			/>

			<AssignLotModal
				visible={assignLotOpen}
				associationNameRaw={message.associationNameRaw}
				propertyAddress={message.propertyAddress}
				hasLot={!!message.accountId}
				busy={updateMessage.isPending}
				onDismiss={() => setAssignLotOpen(false)}
				onAssign={async (accountId) => {
					try {
						await updateMessage.mutateAsync({ id: messageId, body: { accountId } });
						setAssignLotOpen(false);
						showToast(accountId ? 'Lot assigned' : 'Lot cleared', { variant: 'success' });
					} catch (error) {
						fail(error, 'Could not assign the lot.');
					}
				}}
			/>

			{/*
				Which kind of request. Asked rather than inferred: guessing from the wording
				would put a fence request in front of the deck checklist, and the checklist is
				what staff actually work from.
			*/}
			<RequestTypePicker
				visible={typePickerOpen}
				associationId={message.associationId}
				busy={createApplication.isPending}
				onDismiss={() => setTypePickerOpen(false)}
				onPick={async (requestTypeId) => {
					try {
						const created = await createApplication.mutateAsync({ messageId, requestTypeId });
						setTypePickerOpen(false);
						showToast(`Request #${created.applicationNumber ?? ''} created`.trim(), { variant: 'success' });
						router.push(`/exterior-applications/${created.id}` as Href);
					} catch (error) {
						fail(error, 'Could not create the request.');
					}
				}}
			/>

			{/*
				What the message IS, as opposed to what to create from it. A single choice,
				unlike the label picker below — a message is one kind of thing, and the type
				label is derived from this rather than picked alongside it.
			*/}
			<MessageTypePicker
				visible={formTypePickerOpen}
				current={message.formType ?? null}
				busy={updateMessage.isPending}
				onDismiss={() => setFormTypePickerOpen(false)}
				onPick={changeFormType}
			/>

			{/*
				Reply and Forward render in `ComposerProvider`, not here — see `openReply` above.
				The outcome reporting moved with them: it still distinguishes a real send from a
				gated one, because one cheerful "Sent" toast for both is exactly how someone
				comes to believe a homeowner was emailed when nothing left the building.
			*/}

			{/* Labels are a multi-select, not a status — a message can wear several. */}
			<Modal visible={labelPickerOpen} onDismiss={() => setLabelPickerOpen(false)}>
				<View style={{ padding: 20, minWidth: 300, maxWidth: 420 }}>
					<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 4 }}>Labels</Text>
					{/*
						No Save: each tick is applied immediately, the way Gmail does it, so
						there is nothing pending to commit. Done just closes — but it has to
						EXIST, because these modals deliberately don't close on a backdrop tap
						and without it the dialog is a trap.
					*/}
					<Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 12, fontSize: 12 }}>
						Changes save as you tick them.
					</Text>
					<ScrollView style={{ maxHeight: 360 }}>
						{(allLabels ?? []).map((label) => {
							const on = applied.has(label.slug);
							return (
								<Pressable
									key={label.id}
									onPress={() => toggleLabel(label.slug, !on)}
									disabled={setLabel.isPending}
									testID={`toggle-label-${label.slug}`}
									style={{
										flexDirection: 'row',
										alignItems: 'center',
										paddingVertical: 10,
										paddingHorizontal: 8,
										borderRadius: 8,
									}}
								>
									<Ionicons
										name={on ? 'checkbox' : 'square-outline'}
										size={18}
										color={on ? theme.colors.primary : theme.colors.onSurfaceVariant}
										style={{ marginRight: 10 }}
									/>
									<LabelChip label={label} />
								</Pressable>
							);
						})}
					</ScrollView>
					<View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 16 }}>
						<Button
							mode="contained"
							onPress={() => setLabelPickerOpen(false)}
							disabled={setLabel.isPending}
							testID="btn-labels-done"
						>
							Done
						</Button>
					</View>
				</View>
			</Modal>
		</>
	);
};

export default InboxMessagePane;
